<template>
    <div class="flex ml-0 flex-column w-full md:w-1/3 lg:w-1/3 pb-2">
        <vs-input class="border-grey-light w-full" type="text" v-model="interalApplication.name"
            :label="$t('admin.apps.main.name')" :danger="interalApplication.name == ''"/>

        <vs-checkbox class="ml-0 mb-4 mt-4 text-md font-bold text-dark" v-model="interalApplication.is_ski_app">
            {{ $t('admin.apps.main.is_ski_app') }}
        </vs-checkbox>

        <vs-select class="border-grey-light pb-2 w-full" v-model="interalApplication.status" required
            :label="$t('admin.apps.main.status')">
            <vs-select-item :key="option.key" :value="option.key" :text="$t(option.label) | capitalize"
                v-for="option in statusOptions" />
        </vs-select>

        <div class="vs-component vs-con-input-label vs-input w-full border-grey-light vs-input-primary" >
            <label for="" class="vs-input--label">{{ $t('admin.apps.main.status-comments') }}</label>
            <div class="vs-con-input">
                <textarea class="w-full h-32 rounded resize-none border-grey-light p-2"
                    v-model="interalApplication.status_comments"
                />
            </div>
        </div>

        <CreatableSelect
            v-model="interalApplication.community_uuid"
            :options="communities.map(c => ({ key: c.uuid, text: $options.filters.capitalize(c.name) }))"
            :label="$t('admin.apps.main.community')"
            create-route-name="admin-communities-edit"
            :reload="reloadCommunities"
            :resolve-created-key="payload => payload.uuid"
            @reloaded="list => communities = list"
        />

        <vs-input v-if="!hasSkitudeCommunity"
                  class="border-grey-light w-full"
                  type="text"
                  v-model="interalApplication.link_web"
                  :label="$t('admin.apps.main.link-web')"
                  :danger="interalApplication.link_web == ''"
        />

        <CreatableSelect
            v-model="interalApplication.conditions_id_ref"
            :options="conditions.map(r => ({ key: r.reference, text: r.reference }))"
            :label="$t('admin.apps.main.id-ref')"
            create-route-name="admin-app-condition-create"
            :reload="reloadConditions"
            :resolve-created-key="payload => payload.reference"
            :created-to-option="payload => ({ key: payload.reference, text: payload.reference })"
            @reloaded="list => conditions = list"
        />

        <Combobox
            v-model="interalApplication.interactive_map_id"
            :options="[{id: null, name: $t('admin.apps.main.none')}, ...interactiveMaps].map(map => ({ key: map.id, text: map.name }))"
            :label="$t('admin.apps.main.interactive-map')"
        />

        <vs-input class="border-grey-light w-full" type="text" v-model="interalApplication.scheme"
            :label="$t('admin.apps.main.scheme')"
            :danger="!isValidScheme"
            :danger-text="$t('admin.apps.main.scheme-invalid')" />

        <Combobox
            v-model="interalApplication.languages"
            :multiple="true"
            :options="spotlioLanguages.map(r => ({ key: parseInt(r.id), text: r.name }))"
            :label="$t('admin.apps.main.languages')"
        />

        <vs-select v-if="mainLanguages.length > 0" class="border-grey-light pb-2 w-full" v-model="interalApplication.main_language" required
            :label="$t('admin.apps.main.main-language')">
            <vs-select-item :key="option.key" :value="option.key" :text="option.label | capitalize"
                v-for="option in mainLanguages"
            />
        </vs-select>

        <vs-select class=" pb-2 w-full" v-model="interalApplication.country_id" required
            :label="$t('admin.apps.main.country')">
            <vs-select-item :key="country.id" :value="parseInt(country.id)" :text="country.name | capitalize"
                v-for="country in countries"
            />
        </vs-select>

        <vs-select class="border-grey-light pb-2 w-full" v-model="interalApplication.coordinates_format" required
            :label="$t('admin.apps.main.coordinates-format')">
            <vs-select-item :key="option.key" :value="option.key" :text="$t(option.label) | capitalize"
                v-for="option in coordinatesFormatOptions" />
        </vs-select>

        <vs-checkbox class="ml-0 mb-4 mt-4 text-md font-bold text-dark" v-model="hasSummerMode" @change="handleSummerMode">
            {{ $t('admin.apps.main.has-summer-mode') }}
        </vs-checkbox>

        <vs-input v-if="hasSummerMode" :danger="hasSummerMode && interalApplication.season_version === ''" class="border-grey-light w-full" type="number" step="1" v-model="interalApplication.season_version"
            :label="$t('admin.apps.main.summer-start-version')" required />

        <vs-input v-if="hasSummerMode" :danger="hasSummerMode && interalApplication.winter_start_date == ''" class="border-grey-light w-full" type="text" v-model="winter_start_date"
            :label="$t('admin.apps.main.winter-start-date')" required />

        <vs-input v-if="hasSummerMode" :danger="hasSummerMode && winter_end_date == ''" class="border-grey-light w-full" type="text" v-model="winter_end_date"
            :label="$t('admin.apps.main.winter-end-date')" required />

        <div v-if="hasSkitudeCommunity" class="mt-4">
            <vs-checkbox class="ml-0 mb-4 mt-4 text-md font-bold text-dark" v-model="interalApplication.publish_in_web" @change="handleSummerMode">
                {{ $t('admin.apps.main.publish-in-web') }}
            </vs-checkbox>

            <ImageForm
                v-if="interalApplication.publish_in_web"
                id="splashImage"
                :validationWidthEqualsHeight="false"
                validationMessage="Image must be a png with 291x414 px"
                constraintsText="Png 291x414 px"
                width="194"
                height="276"
                :valid-sizes="[{width: 291, height: 414}]"
                :src="interalApplication.splash_image"
                @updateImage="handleUploadSplashImage"
                name="splashImage"/>
        </div>

    </div>
</template>

<script>
import CommunityService from "@/modules/Admin/Services/CommunityService";
import Combobox from "@/modules/Shared/Components/Combobox.vue";
import CreatableSelect from "@/modules/Shared/Components/CreatableSelect.vue";
import CountryService from "@/modules/Shared/Services/CountryService";
import { mapGetters } from "vuex";
import loader from "@/modules/Shared/Mixins/loader";
import {unifyPaginatedResponse} from "@/modules/Shared/Helpers/apiResponseHelper";
import ApplicationService from "@/modules/Admin/Services/ApplicationService";
import ImageForm from "@/modules/Shared/Components/form/ImageForm.vue";
import AssetsService from "@/modules/Shared/Services/AssetsService";
import AppConditionsService from "@/modules/Admin/Services/AppConditionsService";
import InteractiveMapsService from "@/modules/Admin/Services/InteractiveMapsService";
import { urlSchemeValidation } from "@/modules/Shared/Helpers/validationHelper";

export default {
  name: "MainSettings",
  components: {ImageForm, Combobox, CreatableSelect },
  mixins: [loader],
  props: {
    application: {
        required: true,
    }
  },
  data() {
    return {
        createsApplication: true,
        uploaded_splash_image: null,
        interalApplication: {
            name: '',
            is_ski_app: false,
            status: 'development',
            status_comments: '',
            community_uuid: null,
            conditions_id_ref: '',
            scheme: '',
            languages: [ 1 ],
            main_language: 1,
            country_id: null,
            season_version: null,
            winter_start_month: null,
            winter_start_day: null,
            winter_end_month: null,
            winter_end_day: null,
            interactive_map_id: null,
            coordinates_format: 'dms',
        },
        hasSummerMode: false,
        statusOptions: [
            { key: 'development', label: 'admin.apps.main.status-development' },
            { key: 'production', label: 'admin.apps.main.status-production' },
            { key: 'onhold', label: 'admin.apps.main.status-onhold' },
            { key: 'cancelled', label: 'admin.apps.main.status-cancelled' }
        ],
        coordinatesFormatOptions: [
            { key: 'dms', label: 'admin.apps.main.coordinates-format-dms' },
            { key: 'dd', label: 'admin.apps.main.coordinates-format-dd' },
        ],
        communities: [ ],
        countries: [ ],
        conditions: [ ],
        interactiveMaps: [ ],
        winter_start_date: '11-01',
        winter_end_date: '05-01',
        link_web: null,
        publish_in_web: false,
        splash_image: null,
    }
  },
  async created() {
    try {
        this.createsApplication = this.application === null
        if (!this.createsApplication) {
            this.interalApplication = this.application
            this.hasSummerMode = this.interalApplication.season_version !== null
            if (this.hasSummerMode) {
                this.winter_start_date = this.getWinterStartDateString()
                this.winter_end_date = this.getWinterEndDateString()
            }
            this.interalApplication.languages = Array.from(new Set(this.application.languages.map(r => parseInt(r.id))))
        }
    } catch (e) {
        console.error(e)
    }
    await this.loadAndNotifyError(async () => {
        const [, , countries, interactiveMaps] = await Promise.all([
            this.reloadCommunities(),
            this.reloadConditions(),
            unifyPaginatedResponse(CountryService.getCountries, {per_page: 30}),
            unifyPaginatedResponse(InteractiveMapsService.getInteractiveMaps, {q: 'type:3d'})
        ])

        this.countries = countries
        this.interactiveMaps = interactiveMaps
        this.setSpainAsDefaultCountry()
    })
  },
  computed: {
    ...mapGetters(['spotlioLanguages']),
    mainLanguages() {
        return this.spotlioLanguages
            .filter((l) => this.interalApplication.languages.includes(l.id))
            .map(r => ({ key: parseInt(r.id), label: r.name }))
    },
    hasSkitudeCommunity() {
        return this.interalApplication.community_uuid === "ebda2cd8-afe7-4254-a55f-c73344f6fef2"
    },
    isValidScheme() {
        return urlSchemeValidation(this.interalApplication.scheme)
    },
  },
  watch: {
    winter_start_date(value) {
        const [month, day] = value.split('-')
        this.interalApplication.winter_start_month = parseInt(month)
        this.interalApplication.winter_start_day = parseInt(day)
    },
    winter_end_date(value) {
        const [month, day] = value.split('-')
        this.interalApplication.winter_end_month = parseInt(month)
        this.interalApplication.winter_end_day = parseInt(day)
    },
  },
  methods: {
    getWinterStartDateString() {
        if (!this.interalApplication.winter_start_month || !this.interalApplication.winter_start_day) {
            return null
        }
        return `${this.interalApplication.winter_start_month.toString().padStart(2, "0")}-${this.interalApplication.winter_start_day.toString().padStart(2, "0")}`
    },
    getWinterEndDateString() {
        if (!this.interalApplication.winter_end_month || !this.interalApplication.winter_end_day) {
            return null
        }
        return `${this.interalApplication.winter_end_month.toString().padStart(2, "0")}-${this.interalApplication.winter_end_day.toString().padStart(2, "0")}`
    },
    async handleUploadSplashImage(file) {
        const binaryAsset = new Blob([file]);

        let name = `${Date.now()}__${file.name}`
        let presignedUrl = await AssetsService.getTempAssetResource(name)
        presignedUrl = presignedUrl.url
        await AssetsService.uploadFileInS3Resource(presignedUrl, binaryAsset)

        this.uploaded_splash_image = name

        return presignedUrl
    },
    handleSummerMode(hasSummerMode) {
        if (hasSummerMode.target.checked) {
            this.interalApplication.season_version = 0
            this.interalApplication.winter_start_month = 11
            this.interalApplication.winter_start_day = 1
            this.interalApplication.winter_end_month = 5
            this.interalApplication.winter_end_day = 1
        } else {
            this.interalApplication.season_version = null
            this.interalApplication.winter_start_month = null
            this.interalApplication.winter_start_day = null
            this.interalApplication.winter_end_month = null
            this.interalApplication.winter_end_day = null
        }
    },
    async reloadCommunities() {
        this.communities = await unifyPaginatedResponse(CommunityService.getCommunities)
        this.setSkitudeCommunityToFirstPlace()
        return this.communities
    },
    async reloadConditions() {
        this.conditions = await unifyPaginatedResponse(AppConditionsService.getAppConditions, {q: 'current:1'})
        return this.conditions
    },
    setSkitudeCommunityToFirstPlace() {
        this.communities.sort((a, b) => a.name.localeCompare(b.name))
        const index = this.communities.findIndex((c) => c.name == 'Skitude')
        const skitude = this.communities[index]
        this.communities.splice(index, 1)
        this.communities.splice(0, 0, skitude)
    },
    setSpainAsDefaultCountry() {
        this.countries.sort((a, b) => a.name.localeCompare(b.name))
    },
    prepareApplicationPayload() {
        if (this.hasSkitudeCommunity) {
            this.interalApplication.splash_image = this.interalApplication.publish_in_web && this.uploaded_splash_image
                ? this.uploaded_splash_image
                : null
            this.link_web = null
        } else {
            this.interalApplication.publish_in_web = false
            this.interalApplication.splash_image = null
        }
    },
    save() {
        if (!this.isValidScheme) {
            this.notifyError(this.$t('shops.error.0'), this.$t('admin.apps.main.scheme-invalid'))
            return Promise.reject(new Error('Invalid scheme'))
        }
        this.prepareApplicationPayload()
        return new Promise(async (resolve, reject) => {
            this.loadAndNotifyError(async () => {
                if (this.createsApplication) {
                    return ApplicationService.createApplication(this.$route.params.uuid, this.interalApplication)
                } else {
                    return ApplicationService.updateApplication(this.$route.params.uuid, this.interalApplication)
                }
            }).then((result) => {
                if (!result) {
                    reject()
                    return;
                }
                resolve()
            })
        })
    }
  }
};
</script>
